import { useEffect, useRef } from "react";

// elementRef = Excepts reference to HTML element
// callback = Function that will fire when user clicks outside of referenced elements
// exceptionRef = Optional, allows you to add another reference that cancels callback if the user clicks on it and it is outside of the element ref.
// exceptionBool = Optional, allows you to add a bool that cancels callback if the exception bool is true.
// IMPORTANT - Only use ONE exception!
const useClickOutside = (
  elementRef: React.RefObject<HTMLElement>,
  callback: (e: MouseEvent) => void,
  exceptionRef: any = null,
  exceptionBool: any = null
) => {
  const callbackRef = useRef<(e: MouseEvent) => void>();
  callbackRef.current = callback;

  const exceptionBoolRef = useRef();
  exceptionBoolRef.current = exceptionBool;

  // Development message for explaining crash
  if (
    (elementRef && elementRef?.current?.hasOwnProperty("props")) ||
    (exceptionRef && exceptionRef?.current?.hasOwnProperty("props"))
  ) {
    console.error(
      "Invalid ref(s) passed to useClickOutside. You referenced a functional component, ensure you are referencing an html element."
    );
  }

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        elementRef?.current?.contains(e.target as Node) === false &&
        typeof callbackRef.current === "function"
      ) {
        let isExceptionRef = exceptionRef !== null;
        let isExceptionBool = exceptionBoolRef.current !== null;

        if (isExceptionRef && isExceptionBool) {
          console.error(
            "You provided 2 exceptions to useClickOutside hook! Ensure you are only using one exception."
          );
        }

        if (!isExceptionRef && !isExceptionBool) {
          callbackRef.current(e);
          return;
        }

        if (isExceptionRef) {
          if (exceptionRef?.current?.contains(e.target) === false) {
            callbackRef.current(e);
            return;
          }
        } else {
          if (exceptionBoolRef.current === false) {
            callbackRef.current(e);
            return;
          }
        }
      } else if (typeof callbackRef.current !== "function") {
        console.log("You are not passing a function to useClickOutside hook.");
      }
    };

    document.addEventListener("click", handleClickOutside, true);

    return () =>
      document.removeEventListener("click", handleClickOutside, true);
  }, [elementRef, exceptionRef]);
};

export default useClickOutside;
